import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createYouTubeSegment, getVideoSegments, deleteYouTubeSegment, updateYouTubeSegment } from '@/services/youtube.service'
import type { CreateYouTubeSegmentRequest, UpdateYouTubeSegmentRequest } from '@/types/youtubeSegments.types'

/**
 * Query key for video-scoped YouTube segment lists.
 * Scoped to [YOUTUBE_SEGMENTS_QUERY_KEY, videoId] for targeted invalidation.
 */
export const YOUTUBE_SEGMENTS_QUERY_KEY = 'youtube-segments'

/**
 * useVideoSegments — TanStack Query hook for loading segments on the player page.
 *
 * Fetches all segments for a saved YouTube video by its internal UUID.
 * Only fires when videoId is a non-empty string.
 */
export const useVideoSegments = (videoId: string) => {
  return useQuery({
    queryKey: [YOUTUBE_SEGMENTS_QUERY_KEY, videoId],
    queryFn: () => getVideoSegments(videoId),
    select: (data) => data.segments,
    enabled: !!videoId,
  })
}

/**
 * useCreateYouTubeSegment — mutation hook for posting a new YouTube segment.
 *
 * On success, invalidates the segment list for the target video so the
 * VideoPlayerSegmentList re-fetches and shows the new entry immediately.
 *
 * Toast notifications are NOT generated here in line with the architecture rule
 * that prohibits study-flow interruptions (project-context.md, anti-patterns in story Dev Notes).
 */
export const useCreateYouTubeSegment = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: CreateYouTubeSegmentRequest) => createYouTubeSegment(request),
    onSuccess: (data) => {
      if (data.status === 'success' && data.segment) {
        // Invalidate the segment list for the affected video so the list re-fetches.
        queryClient.invalidateQueries({
          queryKey: [YOUTUBE_SEGMENTS_QUERY_KEY, data.segment.video_id],
        })
      }
    },
  })
}

/**
 * useDeleteYouTubeSegment — mutation hook for deleting a YouTube segment.
 *
 * On success, invalidates the segment list for the target video so the
 * VideoPlayerSegmentList re-fetches and removes the deleted entry immediately.
 *
 * Toast notifications are NOT generated here in line with the architecture rule
 * that prohibits study-flow interruptions.
 */
export const useDeleteYouTubeSegment = (videoId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (segmentId: number) => deleteYouTubeSegment(segmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [YOUTUBE_SEGMENTS_QUERY_KEY, videoId],
      })
    },
  })
}

/**
 * useUpdateYouTubeSegment — mutation hook for updating a YouTube segment's times.
 *
 * On success, invalidates the segment list for the target video so the
 * VideoPlayerSegmentList re-fetches with the updated times.
 *
 * No toast notifications per architecture rule against study-flow interruptions.
 */
export const useUpdateYouTubeSegment = (videoId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      segmentId,
      request,
    }: {
      segmentId: number
      request: UpdateYouTubeSegmentRequest
    }) => updateYouTubeSegment(segmentId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [YOUTUBE_SEGMENTS_QUERY_KEY, videoId],
      })
    },
  })
}
